import { Fragment, type ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { type Xor, cn } from '@dinewise/utils';
import { Transition } from '@headlessui/react';
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  XCircleIcon,
  XMarkIcon,
} from '@heroicons/react/20/solid';
import {
  CheckCircleIcon as CheckCircleOutlineIcon,
  XMarkIcon as XMarkOutlineIcon,
} from '@heroicons/react/24/outline';
import Markdown from 'markdown-to-jsx';
import ms from 'ms';
import { ABOVE_MODAL_LAYER_Z_CLASS_NAME } from '../modal-layer.tsx';
import { CopyLinkButton } from './actions.tsx';
import { type ActionableErrorKey, ErrorKeyActions } from './error-actions.tsx';

type NotificationCopyLinkActionProps = {
  action: 'CopyLinkAction';
  url: string;
  preClickText?: string;
  postClickText?: string;
};
type NotificationErrorActionProps = {
  action?: ActionableErrorKey | null;
  url?: never;
  preClickText?: never;
  postClickText?: never;
};
type NotificationActionProps = Xor<NotificationCopyLinkActionProps, NotificationErrorActionProps>;

type StickyNotificationProps = {
  sticky?: true;
  timeout?: never;
};
type NonStickyNotificationProps = {
  sticky?: false;
  timeout?: number;
};
type NotificationDurationProps = Xor<StickyNotificationProps, NonStickyNotificationProps>;

type NotificationAsToastProps = {
  classNames?: {
    notificationAsToast?: string;
    fixedOverlay?: boolean;
    body?: string;
    bodyContainer?: string;
    container?: string;
  };
};

export type NotificationToastVariant = 'default' | 'prominent' | 'pill';

type NotificationToastVariantStyles = {
  overlay: string;
  stack: string;
  toast: string;
  bodyContainer: string;
  iconWrapper: string;
  icon: string;
  content: string;
  contentWithViewEventAction: string;
  title: string;
  body: string;
  closeWrapper: string;
};

const defaultToastChrome = {
  overlay:
    'pointer-events-none fixed inset-0 max-h-[64px] flex items-end px-2 py-3 sm:items-start sm:p-6',
  stack: 'items-center space-y-4 sm:items-end',
  toast: 'w-full max-w-sm',
  closeWrapper: 'ml-4 flex flex-shrink-0',
} as const;

const notificationToastVariantStyles = {
  default: {
    ...defaultToastChrome,
    bodyContainer:
      'flex items-center justify-start rounded-lg bg-ande-black p-4 ring-1 ring-black ring-opacity-5',
    iconWrapper: 'flex size-10 flex-shrink-0 items-center justify-center rounded-xl',
    icon: 'size-6 rounded-md',
    content: 'ml-3 flex w-0 flex-1 flex-col justify-center gap-4 pt-0.5',
    contentWithViewEventAction: 'flex-row',
    title: 'font-semibold text-white',
    body: 'text-white text-xs',
  },
  prominent: {
    ...defaultToastChrome,
    bodyContainer:
      'flex items-start justify-start rounded-xl bg-ande-black p-3 ring-1 ring-black ring-opacity-5',
    iconWrapper:
      'flex size-10 flex-shrink-0 items-center justify-center rounded-xl bg-ande-success',
    icon: 'size-6 rounded-md',
    content: 'ml-2.5 flex w-0 flex-1 flex-col justify-center gap-2 pt-0',
    contentWithViewEventAction: '',
    title: 'font-semibold text-white text-sm leading-5',
    body: 'text-white text-xs mt-0.5 leading-4',
  },
  pill: {
    overlay: 'pointer-events-none fixed inset-0 flex items-start justify-end px-4 pt-6 sm:p-6',
    stack: 'items-end',
    toast: 'w-max max-w-xl',
    bodyContainer: 'flex items-center rounded-full bg-ande-black py-2 pr-5 pl-2 shadow-lg',
    iconWrapper:
      'flex size-12 flex-shrink-0 items-center justify-center rounded-2xl bg-ande-success',
    icon: 'size-6',
    content: 'ml-3 flex min-w-0 flex-1 flex-col justify-center',
    contentWithViewEventAction: '',
    title: 'font-medium text-white text-sm leading-5',
    body: 'mt-0.5 text-ande-gray-200 text-xs leading-4',
    closeWrapper: 'ml-8 flex flex-shrink-0',
  },
} satisfies Record<NotificationToastVariant, NotificationToastVariantStyles>;

export type NotificationType = {
  type: 'success' | 'warning' | 'error' | 'info';
  toastVariant?: NotificationToastVariant;
  applyTheme?: boolean;
  title?: string;
  body: string;
  time: number;
  data?: Record<string, unknown>;
  allowDismiss?: boolean;
} & NotificationActionProps &
  NotificationDurationProps &
  NotificationAsToastProps;

type NotificationProps = {
  notification: NotificationType | null;
  onHidden?: () => void;
  closeLabel?: string;
};

type InnerNotificationProps = {
  notification: NotificationType;
  linkComponent?: ReactNode;
  onHidden?: () => void;
  closeLabel?: string;
};

const getIcon = (type: NotificationType['type']) => {
  switch (type) {
    case 'success': {
      return CheckCircleIcon;
    }
    case 'error': {
      return XCircleIcon;
    }
    case 'info': {
      return InformationCircleIcon;
    }
    case 'warning': {
      return ExclamationTriangleIcon;
    }
    default: {
      const _exhaustive: never = type;
      throw new Error(`Unhandled notification type: ${_exhaustive}`);
    }
  }
};

const getStyles = (type: NotificationType['type']) => {
  switch (type) {
    case 'success': {
      return {
        iconClass: 'text-ande-white',
        iconBackground: 'bg-ande-success',
        titleClass: 'text-ande-white',
      };
    }
    case 'error': {
      return {
        iconClass: 'text-ande-white',
        iconBackground: 'bg-ande-error',
        titleClass: 'text-ande-white',
      };
    }
    case 'info': {
      return {
        iconClass: 'text-ande-white',
        iconBackground: 'bg-ande-surface-500',
        titleClass: 'text-ande-white',
      };
    }
    case 'warning': {
      return {
        iconClass: 'text-ande-black',
        iconBackground: 'bg-ande-warning',
        titleClass: 'text-ande-white',
      };
    }
    default: {
      const _exhaustive: never = type;
      throw new Error(`Unhandled notification type: ${_exhaustive}`);
    }
  }
};

const defaultTimeoutMs = ms('5 seconds');

const InnerNotification = ({
  notification,
  onHidden,
  closeLabel = 'Close',
}: InnerNotificationProps) => {
  const {
    type,
    title,
    body,
    time,
    action,
    data,
    url,
    preClickText,
    postClickText,
    applyTheme,
    sticky,
    timeout,
    allowDismiss = true,
    classNames,
    toastVariant = 'default',
  } = notification;
  const variantStyles = notificationToastVariantStyles[toastVariant];
  const timeoutMs = timeout ?? defaultTimeoutMs;
  const [show, setShow] = useState(true);
  const [isActionVisible, setIsActionVisible] = useState(true);
  const isHoveredRef = useRef(false);
  const dismissDeferredRef = useRef(false);

  const { theme, Icon } = useMemo(() => {
    if (applyTheme) {
      return {
        theme: getStyles(type),
        Icon: getIcon(type),
      };
    }

    return {
      theme: null,
      Icon: null,
    };
  }, [applyTheme, type]);

  const dismiss = () => {
    if (sticky) {
      return;
    }
    setShow(false);
    onHidden?.();
  };

  // oxlint-disable-next-line react-doctor/no-derived-state-effect -- intentionally syncs external inputs into local UI or form state
  useEffect(() => {
    setShow(true);
    setIsActionVisible(true);
  }, [type, title, body, time, action]);

  useEffect(() => {
    if (sticky) {
      return;
    }

    setShow(true);
    dismissDeferredRef.current = false;

    const timeoutId = setTimeout(() => {
      if (isHoveredRef.current) {
        dismissDeferredRef.current = true;

        return;
      }
      setShow(false);
      onHidden?.();
    }, timeoutMs);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [timeoutMs, sticky, onHidden]);

  const LinkedActionComponent = useMemo(() => {
    if (!action || !isActionVisible) {
      return null;
    }

    if (action === 'CopyLinkAction') {
      return (
        <CopyLinkButton
          {...{ url, preClickText, postClickText }}
          classNames={{
            text: cn(applyTheme ? theme?.iconClass : null),
          }}
        />
      );
    }

    const Component = ErrorKeyActions[action];

    return (
      <Component
        {...{ data, toastVariant }}
        onCloseAction={() => {
          onHidden?.();
          setIsActionVisible(false);
        }}
      />
    );
  }, [
    action,
    isActionVisible,
    data,
    toastVariant,
    url,
    preClickText,
    postClickText,
    applyTheme,
    theme?.iconClass,
    onHidden,
  ]);

  const usesFixedOverlay = classNames?.fixedOverlay ?? true;
  const overlayClassName = usesFixedOverlay
    ? cn(variantStyles.overlay, ABOVE_MODAL_LAYER_Z_CLASS_NAME)
    : '';
  const stackClassName = classNames?.notificationAsToast ?? variantStyles.stack;
  const toastWidthClassName = classNames?.notificationAsToast
    ? 'w-full max-w-full'
    : variantStyles.toast;
  const isPill = toastVariant === 'pill';
  const SuccessIcon = isPill ? CheckCircleOutlineIcon : CheckCircleIcon;
  const CloseIcon = isPill ? XMarkOutlineIcon : XMarkIcon;

  return (
    <div aria-live="assertive" className={cn(overlayClassName, classNames?.container)}>
      <div className={cn('flex w-full flex-col', stackClassName)}>
        <Transition
          show={show}
          as={Fragment}
          enter="transform ease-out duration-300 transition"
          enterFrom={
            isPill
              ? 'translate-y-2 opacity-0'
              : 'translate-y-2 opacity-0 sm:translate-y-0 sm:translate-x-2'
          }
          enterTo={
            isPill ? 'translate-y-0 opacity-100' : 'translate-y-0 opacity-100 sm:translate-x-0'
          }
          leave="transition ease-in duration-100"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div
            className={cn('pointer-events-auto overflow-hidden', toastWidthClassName)}
            onMouseEnter={() => {
              isHoveredRef.current = true;
            }}
            onMouseLeave={() => {
              isHoveredRef.current = false;
              if (!dismissDeferredRef.current) {
                return;
              }
              dismissDeferredRef.current = false;
              dismiss();
            }}
          >
            <div className={cn(variantStyles.bodyContainer, classNames?.bodyContainer)}>
              {Icon || type ? (
                <div className={cn(variantStyles.iconWrapper, theme?.iconBackground)}>
                  {Icon ? (
                    <Icon className={cn(variantStyles.icon, theme?.iconClass)} />
                  ) : (
                    <>
                      {type === 'success' ? (
                        <SuccessIcon className="size-6 text-ande-white" aria-hidden="true" />
                      ) : null}
                      {type === 'info' ? (
                        <InformationCircleIcon
                          className="size-6 text-ande-white"
                          aria-hidden="true"
                        />
                      ) : null}
                      {type === 'warning' ? (
                        <ExclamationTriangleIcon
                          className="size-6 fill-ande-warning"
                          aria-hidden="true"
                        />
                      ) : null}
                      {type === 'error' ? (
                        <XCircleIcon className="size-6 fill-ande-error" aria-hidden="true" />
                      ) : null}
                    </>
                  )}
                </div>
              ) : null}
              <div
                className={cn(
                  variantStyles.content,
                  action === 'ViewEvent' ? variantStyles.contentWithViewEventAction : null,
                )}
              >
                <div>
                  {title ? (
                    <p className={cn(variantStyles.title, theme?.titleClass)}>{title}</p>
                  ) : null}
                  {body ? (
                    <p className={cn(variantStyles.body, classNames?.body)}>
                      <Markdown>{body}</Markdown>
                    </p>
                  ) : null}
                </div>
                {LinkedActionComponent}
              </div>
              {allowDismiss ? (
                <div className={variantStyles.closeWrapper}>
                  <button
                    type="button"
                    className="inline-flex rounded-md bg-transparent text-ande-white hover:text-ande-surface-100 focus:outline-none focus:ring-2 focus:ring-ande-orange-400 focus:ring-offset-2"
                    onClick={() => {
                      setShow(false);
                      onHidden?.();
                    }}
                  >
                    <span className="sr-only">{closeLabel}</span>
                    <CloseIcon className="size-5" aria-hidden="true" />
                  </button>
                </div>
              ) : null}
            </div>
          </div>
        </Transition>
      </div>
    </div>
  );
};

export const Notification = ({ notification, onHidden, closeLabel }: NotificationProps) => {
  if (!notification) {
    return null;
  }

  return <InnerNotification {...{ notification, onHidden, closeLabel }} />;
};
